import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { translate, TranslatorKeys } from "@/config/translator";
import { useAttendanceMobile } from "./attendanceMobile/useAttendanceMobile";
import { AttendanceMobileElement } from "./attendanceMobile/attendanceMobileElement";
import PspButton from "@/components/pspButton/PspButton";
// eslint-disable-next-line max-len
import {
  faArrowRotateLeft,
  faClockRotateLeft,
  faPaperPlaneTop,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MobileBottomSheet } from "@/components/mobileBottomSheet/mobileBottomSheet";
import { Dispatch, SetStateAction } from "react";
import PspSelect from "@/components/pspSelect/pspSelect";
import { MessageBar } from "@fluentui/react-components";
import { formatDateFromLang } from "@/config/utils";
import { PressableBoxMobile } from "@/components/pressableBoxMobile/pressableBoxMobile";

type AttendanceMobileProps = {
  openSubmitAttendanceConfirmation: () => void;
  setRestoreOriginalConfirmationOpen: Dispatch<SetStateAction<boolean>>;
  canRestoreOriginal: boolean;
};

export function AttendanceMobile({
  openSubmitAttendanceConfirmation,
  setRestoreOriginalConfirmationOpen,
  canRestoreOriginal,
}: AttendanceMobileProps) {
  const {
    lang,
    versionRestoreBottomSheetOpen,
    setVersionRestoreBottomSheetOpen,
    navigate,
    attendanceMonth,
    onSaveDraft,
    availableMonths,
    onSelectMonth,
    selectedMonth,
    selectedYear,
    isSubmitted,
    submittedAt,
  } = useAttendanceMobile();

  return (
    <>
      <TopBarMobile
        rightSide={
          <button onClick={() => setVersionRestoreBottomSheetOpen(true)}>
            <FontAwesomeIcon
              icon={faClockRotateLeft}
              className="text-font-white text-lg"
            />
          </button>
        }
        headerString={translate("attendances", lang)}
      />
      <div className="p-6 pb-36">
        <div className="w-60 mb-6">
          {availableMonths && (
            <PspSelect
              onChange={onSelectMonth}
              value={`${selectedMonth} ${selectedYear}`}
            >
              {availableMonths?.map((m) => (
                <option value={`${m.month} ${m.year}`}>
                  {translate(`month${m.month - 1}` as TranslatorKeys, lang)}{" "}
                  {m.year}
                </option>
              ))}
            </PspSelect>
          )}
        </div>
        {isSubmitted && submittedAt && (
          <div className="pb-3">
            <MessageBar>
              <div>
                <span>{translate("alreadySubmittedOn", lang)} </span>
                <span className="font-semibold">
                  {formatDateFromLang(submittedAt, lang)}
                </span>
              </div>
            </MessageBar>
          </div>
        )}
        <div>
          {attendanceMonth?.attendances.map(
            (attendance, index, attendancesArray) => (
              <>
                <AttendanceMobileElement attendance={attendance} />
                {index !== attendancesArray.length - 1 && (
                  <div className="h-px bg-neutral my-2 -mx-6" />
                )}
              </>
            ),
          )}
        </div>
      </div>
      <div className="fixed bottom-20 p-4 w-full flex flex-col gap-3 h-fit">
        <PspButton
          disabled={isSubmitted || isSubmitted === undefined}
          onClick={onSaveDraft}
          appearance="secondary"
        >
          {translate("save", lang)}
        </PspButton>
        <PspButton
          appearance="primary"
          disabled={isSubmitted || isSubmitted === undefined}
          onClick={openSubmitAttendanceConfirmation}
        >
          <div className="flex flex-row gap-3 items-center">
            <div>{translate("submitAttendance", lang)}</div>
            <FontAwesomeIcon
              icon={faPaperPlaneTop}
              className="text-font-white text-base"
            />
          </div>
        </PspButton>
      </div>
      <MobileBottomSheet
        isOpen={versionRestoreBottomSheetOpen}
        onClose={() => setVersionRestoreBottomSheetOpen(false)}
        title={translate("versions", lang)}
      >
        <div className="flex flex-col gap-2">
          <PressableBoxMobile
            primaryText={translate("pastVersions", lang)}
            secondaryText={translate("restoreVersionMessage", lang)}
            icon={faClockRotateLeft}
            onClick={() => navigate("/attendances/versions")}
          />
          {canRestoreOriginal && (
            <PressableBoxMobile
              primaryText={translate("restoreOriginal", lang)}
              secondaryText={translate("restoreOriginalMessage", lang)}
              icon={faArrowRotateLeft}
              onClick={() => {
                setRestoreOriginalConfirmationOpen(true);
                setVersionRestoreBottomSheetOpen(false);
              }}
            />
          )}
        </div>
      </MobileBottomSheet>
    </>
  );
}
